"use client";

import React from "react";
import Image from "next/image";
import SafeHtml from "@/components/ui/SafeHtml";
import { CheckIcon } from "@/components/ui/Icons";
import { toAbsoluteUrl } from "@/lib/toAbsoluteUrl";
import type { FeatureHighlightsFields } from "@/types/acf";

export default function FeatureHighlights({ data }: { data: FeatureHighlightsFields }) {
  if (!data || data.hide) return null;

  const header = data.title;
  const title = header?.title ?? "";
  const subtitle = header?.subtitle ?? "";

  const imageObj = header?.image && typeof header.image === "object" ? header.image : null;
  const imageSrc = imageObj?.url ? toAbsoluteUrl(imageObj.url) : null;

  const features = Array.isArray(data.features) ? data.features : [];

  if (!title && !subtitle && !imageSrc && features.length === 0) return null;

  return (
    <div id={data.id} className="w-full">
      {/* Header */}
      <div className="relative overflow-hidden bg-fuji-red p-6 text-white">
        <div className="flex flex-col gap-3 pr-0 sm:pr-32">
          {title ? (
            <h3 className="text-title-3">
              <SafeHtml content={title} />
            </h3>
          ) : null}

          {subtitle ? (
            <div className="text-title-4">
              <SafeHtml content={subtitle} />
            </div>
          ) : null}
        </div>

        {/* Top-right image */}
        {imageSrc ? (
          <div className="mt-6 sm:mt-0 sm:absolute sm:top-1/2 sm:right-6 sm:-translate-y-1/2">
            <div className="relative w-24 h-24 sm:w-32 sm:h-32">
              <Image
                src={imageSrc}
                alt={imageObj?.alt || ""}
                fill
                className="object-contain"
                sizes="128px"
              />
            </div>
          </div>
        ) : null}
      </div>

      {/* Features */}
      {features.length ? (
        <div className="mt-4 flex flex-col gap-3">
          {features.map((item, i) => {
            const text = item?.feature ?? "";
            if (!text) return null;

            return (
              <div
                key={`feature-${i}`}
                className="flex items-center justify-between gap-4 px-5 py-4 bg-fuji-grey"
              >
                <p className="font-semibold">{text}</p>
                <span className="shrink-0">
                  <CheckIcon className="w-5 h-5" />
                </span>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
